import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const applyAuth = useCallback((token, u) => {
    if (token) localStorage.setItem("ely_token", token);
    setUser(u);
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("ely_token");
    if (!token) { setUser(null); return null; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      localStorage.removeItem("ely_token");
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    applyAuth(data.token, data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    applyAuth(data.token, data.user);
    return data.user;
  };

  const exchangeGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    applyAuth(data.token, data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("ely_token");
    setUser(null);
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Return to the page the user is on (e.g. the sales page) so checkout can resume — never /dashboard.
    const back = window.location.pathname || "/";
    const redirectUrl = window.location.origin + back;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const openAuth = (mode = "login") => { setAuthMode(mode); setAuthOpen(true); };
  const closeAuth = () => setAuthOpen(false);

  const value = {
    user, loading, authOpen, authMode,
    login, register, exchangeGoogle, logout, startGoogle, refresh, applyAuth,
    openAuth, closeAuth, setAuthMode,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
